// The error types must match the Error schema in the partner portal API OpenAPI spec.

export type NewPartner = {
  firstName: string;
  lastName: string;
  email: string;
};

export type Auth0Failure = {
  partnerId: number;
  engineeringMessage: string;
};

const bodyField = (body: unknown, field: string): unknown => {
  if (body && typeof body === 'object' && field in body) {
    return (body as Record<string, unknown>)[field];
  }
  return undefined;
};

export const publisherNotFoundMessage = (publisherId: string) =>
  `No publisher with ID ${publisherId} is in Partner Portal. ` +
  'New publishers from chicory-network are copied at 00:00 and 12:00 UTC. ' +
  'If the publisher is new, try again after the next copy.';

// A 502 without the auth0_login_failed type can come from the load balancer and says nothing about
// whether the partner was created, so only the typed 502 offers the Auth0 retry.
export const auth0FailureFromError = (
  status: number,
  body: unknown
): Auth0Failure | null => {
  const partnerId = bodyField(body, 'partnerId');
  const message = bodyField(body, 'message');
  if (
    status !== 502 ||
    bodyField(body, 'type') !== 'auth0_login_failed' ||
    typeof partnerId !== 'number' ||
    typeof message !== 'string'
  ) {
    return null;
  }
  return { partnerId, engineeringMessage: message };
};

export const AUTH0_FAILED_MESSAGE =
  'Auth0 error. The partner was created but cannot sign in yet. Try again.';

// The API message holds the requests that engineering sends, so the admin pastes it unchanged.
export const auth0FailedAgainMessage = (failure: Auth0Failure) =>
  'Auth0 error again. Try again later. If it keeps failing, send this message to engineering:\n' +
  failure.engineeringMessage;

export const addPartnerErrorMessage = (
  status: number,
  body: unknown,
  partner: NewPartner,
  accountName?: string
): string => {
  const type = bodyField(body, 'type');

  if (status === 409 && type === 'partner_email_exists') {
    return `${partner.email} already belongs to a partner.`;
  }
  if (status === 409 && type === 'account_name_exists') {
    return `An account named "${accountName}" already exists. Deleted accounts also count. Choose another name.`;
  }
  if (status === 400 || status === 422) {
    return 'The partner information is not valid. Check the fields and try again.';
  }
  return 'The partner was not added. Try again. If the problem continues, ask engineering.';
};
